
import { setActivePinia } from 'pinia';
import { pinia } from '@/stores';
import { useCheckoutStore } from '@/stores/useCheckoutStore';
import { useAuthStore } from '@/stores/useAuthStore';
import CustomerHelper from '@/helpers/customerHelper';
import CartService from '@/services/cartService';
import router from '@/router';

function getCheckoutStore() {
  setActivePinia(pinia);
  return useCheckoutStore();
}

function getAuthStore() {
  setActivePinia(pinia);
  return useAuthStore();
}
import ErrorModal from '@/components/popup/modal/errorModal.vue';
import DoctorScheduleDateSlider from '@/components/search/doctorScheduleDateSlider.vue';
import DoctorScheduleTimeGrid from '@/components/search/doctorScheduleTimeGrid.vue';
import DoctorScheduleDurationSelector from '@/components/search/doctorScheduleDurationSelector.vue';
import { buildSlotCache, collectSlotDurations } from '@/helpers/doctorScheduleSlotHelper';
import {
  buildStartDateTimeValue,
  parseTimeToMinutesValue,
} from '@/helpers/doctorScheduleTimeHelper';
import { formatScheduleDateParts } from '@/helpers/dateLabelHelper';
import { prebookAppointment } from '@/services/bookingPrebookService';
import { loadPrice } from '@/services/medkusApi/publicBookingsService';
import { resolvePriceWithTaxes } from '@/services/tenantSettingsService';
import { extractErrorMessages } from '@/helpers/errorMessageHelper';
import { buildDoctorResetKey, buildFallbackDateWindow, buildCurrentDates } from '@/helpers/doctorScheduleCalendarHelper';
import {
  buildDisplayTimes,
  getScheduleEntryByDate,
  resolveSlotInfo,
} from '@/helpers/doctorScheduleSlotStateHelper';
import { resolvePriceLabel } from '@/helpers/assetHelper';
import { normalizeBaseUrl } from '@/helpers/tenantUrlHelper';
import onlineIcon from '@/assets/images/online.svg';
import mapPinIcon from '@/assets/images/map_pin.svg';

const buildDefaultDurationSelector = () => ({
  visible: false,
  options: [],
  date: null,
  time: null,
  slotInfo: null,
  startInfo: null,
  prices: {},
  loading: false,
  selectedDuration: null,
});

const normalizeDurationOptions = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }
  const normalized = [];
  options.forEach((value) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0 && !normalized.includes(numeric)) {
      normalized.push(numeric);
    }
  });
  return normalized;
};

export default {
  name: 'DoctorSchedule',
  components: {
    ErrorModal,
    DoctorScheduleDateSlider,
    DoctorScheduleTimeGrid,
    DoctorScheduleDurationSelector,
  },
  emits: ['booking-option-change', 'request-more-dates', 'request-calendar', 'expand'],

  props: {
    isPopup: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    serviceTitle: {
      type: String,
      default: ''
    },
    doctor: {
      type: Object,
      default: () => ({}),
    },
    selectedBookingKey: {
      type: [String, Number],
      default: null,
    },
    datesLoading: {
      type: Boolean,
      default: false,
    },
    baseDate: {
      type: [Date, String],
      default: null,
    },
    scheduleVersion: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      activeCenterKey: null,
      activeBookingKey: null,
      activeAddressIndex: 0,
      currentDateGroupIndex: 0,
      activeDateIndex: 0,
      visibleSchedules: 2,
      bookingInProgress: false,
      bookingInProgressKey: null,
      errorModalVisible: false,
      errorModalMessages: [],
      slotCache: {},
      durationSelector: buildDefaultDurationSelector(),
      pendingSlotSelection: null,
      lastDoctorResetKey: null,
      pendingDateAdvance: null,
      pendingScheduleVersion: null,
      lastKnownTotalDateGroups: 0,
      scheduleRenderKey: 0,
      initialScheduleFirstDate: null,
      canNavigateBackward: false,
    };
  },
  computed: {
    centers() {
      return Array.isArray(this.doctor?.centers) ? this.doctor.centers : [];
    },
    bookingOptions() {
      return Array.isArray(this.doctor?.bookingOptions) ? this.doctor.bookingOptions : [];
    },
    hasSelectedBooking() {
      return this.selectedBookingKey !== null && this.selectedBookingKey !== undefined;
    },
    displayCenters() {
      if (this.centers.length) {
        return this.centers;
      }

      if (!this.bookingOptions.length) {
        return [];
      }

      const fallbackOption = this.bookingOptions[0];
      return [{
        key: fallbackOption.centerKey ?? fallbackOption.key ?? 'center-default',
        centerId: fallbackOption.centerId ?? null,
        tenantId: fallbackOption.tenant?.id ?? this.doctor?.tenantId ?? null,
        tenantIgniteId: fallbackOption.tenant?.igniteId ?? this.doctor?.tenantIgniteId ?? null,
        tenantName: fallbackOption.tenant?.name ?? this.doctor?.tenantName ?? '',
        name: fallbackOption.location?.name
          ?? fallbackOption.tenant?.name
          ?? this.doctor?.tenantName
          ?? (this.$t ? this.$t('Clinic') : 'Clinic'),
        address: fallbackOption.location?.address
          ?? fallbackOption.tenant?.address
          ?? this.doctor?.mainAddress
          ?? '',
        url: fallbackOption.tenant?.url ?? this.doctor?.tenantUrl ?? null,
        variantKeys: this.bookingOptions.map((option) => String(option.key)),
      }];
    },
    activeBookingOptions() {
      return this.filteredBookingOptions(this.activeCenterKey);
    },
    activeBookingOption() {
      if (!this.hasSelectedBooking) {
        return null;
      }
      const options = this.activeBookingOptions;
      if (!options.length) {
        return null;
      }

      const activeKey = this.activeBookingKey !== null && this.activeBookingKey !== undefined
        ? String(this.activeBookingKey)
        : (this.selectedBookingKey !== null && this.selectedBookingKey !== undefined
          ? String(this.selectedBookingKey)
          : null);
      if (activeKey) {
        const explicit = options.find((option) => option.key === activeKey);
        if (explicit) {
          return explicit;
        }
      }

      return options[0];
    },
    addresses() {
      const option = this.activeBookingOption;
      const scheduleVersion = this.scheduleVersion;
      if (!option || !Array.isArray(option.schedule) || option.schedule.length === 0) {
        return undefined;
      }

      const name = option.location?.name
        ?? option.tenant?.name
        ?? this.doctor?.tenantName
        ?? '';
      const address = option.location?.address
        ?? option.tenant?.address
        ?? this.doctor?.mainAddress
        ?? '';

      const schedule = option.schedule;

      return [{
        id: option.centerId ?? option.resourceId ?? 1,
        name,
        location: address,
        icon: null,
        iconClass: '',
        schedule,
        scheduleVersion,
      }];
    },
    totalDatesMax() {
      const docMax = Number(this.doctor?.totalDatesMax ?? 0);
      if (docMax > 0) {
        return docMax;
      }

      const schedule = this.addresses?.[this.activeAddressIndex]?.schedule ?? [];
      return schedule.length;
    },
    totalTimesMax() {
      const docMax = Number(this.doctor?.totalTimesMax ?? 0);
      if (docMax > 0) {
        return docMax;
      }

      return this.activeTimesMax;
    },
    activeTimesMax() {
      const schedule = this.addresses?.[this.activeAddressIndex]?.schedule ?? [];
      const bookingKey = this.activeBookingKey !== null && this.activeBookingKey !== undefined
        ? String(this.activeBookingKey)
        : null;
      return schedule.reduce((acc, item) => {
        const times = buildDisplayTimes(this.slotCache, bookingKey, item?.date ?? null, item?.times ?? []);
        const timesCount = Array.isArray(times) ? times.filter(Boolean).length : 0;
        return Math.max(acc, timesCount);
      }, 0);
    },
    baseVisibleSchedules() {
      return 2;
    },
    totalDateGroups() {
      const schedule = this.addresses?.[this.activeAddressIndex]?.schedule ?? [];
      if (!schedule.length) {
        return buildFallbackDateWindow(this.baseDate, this.currentDateGroupIndex) ? 1 : 0;
      }

      return Math.ceil(schedule.length / 4);
    },
    disablePrev() {
      if (this.currentDateGroupIndex > 0) {
        return false;
      }
      return !this.canNavigateToPreviousWindow();
    },
    currentDates() {
      const schedule = this.addresses?.[this.activeAddressIndex]?.schedule ?? [];
      return buildCurrentDates(
        schedule,
        this.baseDate,
        this.currentDateGroupIndex,
        (iso) => this.formatDate(iso),
      );
    },
    scheduleColumns() {
      const schedule = this.addresses?.[this.activeAddressIndex]?.schedule ?? [];
      if (!schedule.length) {
        const fallback = buildFallbackDateWindow(this.baseDate, this.currentDateGroupIndex);
        if (!fallback) {
          return [];
        }
        return fallback.dates.map((item) => ({
          date: item.iso,
          times: [],
        }));
      }

      const dates = Array.isArray(this.currentDates) ? this.currentDates : [];
      if (!dates.length) {
        return [];
      }
      const bookingKey = this.activeBookingKey !== null && this.activeBookingKey !== undefined
        ? String(this.activeBookingKey)
        : null;
      return dates.map((item) => {
        const entry = getScheduleEntryByDate(schedule, this.slotCache, bookingKey, item.iso);
        if (entry) {
          return entry;
        }
        return {
          date: item.iso,
          times: [],
        };
      });
    },
    scheduleColumnCount() {
      return this.scheduleColumns.length;
    },
    activeLocation() {
      const option = this.activeBookingOption;
      if (!option) {
        return null;
      }

      return {
        name: option.location?.name
          ?? option.tenant?.name
          ?? this.doctor?.tenantName
          ?? '',
        address: option.location?.address
          ?? option.tenant?.address
          ?? this.doctor?.mainAddress
          ?? '',
      };
    },
    activeAllowRemote() {
      const option = this.activeBookingOption;
      if (!option) {
        return null;
      }
      if (option.allowRemote === true) {
        return true;
      }
      if (option.allowRemote === false) {
        return false;
      }
      return null;
    },
    activeModeLabel() {
      if (this.activeAllowRemote === true) {
        return this.$t('Online');
      }
      if (this.activeAllowRemote === false) {
        return this.$t('In-person');
      }
      return '';
    },
    activeLocationLabel() {
      const locationText = this.activeLocation
        ? (this.activeLocation.address || this.activeLocation.name || '')
        : '';
      const allowRemote = this.activeAllowRemote;
      if (allowRemote === true) {
        return this.activeModeLabel;
      }
      if (allowRemote === false) {
        return locationText || this.activeModeLabel;
      }
      return locationText || this.activeModeLabel;
    },
    activeLocationIcon() {
      if (this.activeAllowRemote === true) {
        return onlineIcon;
      }
      return mapPinIcon;
    },
    activeLocationIconAlt() {
      if (this.activeAllowRemote === true) {
        return this.$t('Online');
      }
      return this.$t('Map pin');
    },
    consultationFeeLabel() {
      return this.activeBookingOption?.price ?? this.doctor?.consultationFee ?? '';
    },
    activeResourceId() {
      return this.activeBookingOption?.resourceId ?? this.doctor?.resourceId ?? null;
    },
    activeBookingTypeId() {
      return this.activeBookingOption?.bookingTypeId ?? null;
    },
    activeBookingTypeName() {
      return this.activeBookingOption?.bookingTypeName ?? this.activeBookingOption?.label ?? '';
    },
    activeDurationMinutes() {
      return this.activeBookingOption?.durationMinutes ?? null;
    },
    tenantId() {
      return this.activeBookingOption?.tenant?.id ?? this.doctor?.tenantId ?? null;
    },
    tenantIgniteId() {
      return this.activeBookingOption?.tenant?.igniteId ?? this.doctor?.tenantIgniteId ?? null;
    },
    tenantName() {
      return this.activeBookingOption?.tenant?.name ?? this.doctor?.tenantName ?? this.activeLocation?.name ?? '';
    },
    tenantUrl() {
      const center = this.displayCenters.find((item) => item.key === this.activeCenterKey);
      return this.activeBookingOption?.tenant?.url
        || center?.url
        || this.doctor?.tenantUrl
        || null;
    },
    doctorName() {
      return this.doctor?.name ?? '';
    },
    specialty() {
      return this.doctor?.specialty ?? '';
    },
    defaultTherapy() {
      return this.doctor?.defaultTherapy ?? null;
    },
    defaultTherapyType() {
      return this.doctor?.defaultTherapyType ?? null;
    },
    mainAddress() {
      return this.doctor?.mainAddress ?? this.activeLocation?.address ?? '';
    },
    scheduleLoading() {
      if (!this.activeBookingOption) {
        return this.datesLoading;
      }
      return this.activeBookingOption.scheduleLoading === true;
    },
    isLoggedIn() {
      return getAuthStore().isAuthenticated;
    },
  },
  mounted() {
    this.lastKnownTotalDateGroups = this.totalDateGroups || 0;
  },
  watch: {
    doctor: {
      immediate: true,
      deep: true,
      handler(newDoctor) {
        const resetKey = buildDoctorResetKey(newDoctor);
        if (this.lastDoctorResetKey === resetKey) {
          return;
        }
        this.lastDoctorResetKey = resetKey;
        this.initializeState();
      }
    },
    selectedBookingKey(newValue) {
      const normalized = newValue !== null && newValue !== undefined ? String(newValue) : null;
      if (!normalized) {
        this.activeBookingKey = null;
        this.resetScheduleNavigation();
        this.closeDurationSelector();
        return;
      }
      if (normalized && normalized !== this.activeBookingKey) {
        const targetCenter = this.displayCenters.find((center) => Array.isArray(center?.variantKeys)
          && center.variantKeys.some((key) => String(key) === normalized));
        if (targetCenter && targetCenter.key !== this.activeCenterKey) {
          this.activeCenterKey = targetCenter.key;
        }
        this.activeBookingKey = normalized;
        this.ensureActiveBooking();
        const resolvedKey = this.activeBookingKey !== null && this.activeBookingKey !== undefined
          ? String(this.activeBookingKey)
          : null;
        if (resolvedKey) {
          this.$emit('booking-option-change', resolvedKey, { source: 'prop' });
        }
      }
    },
    activeCenterKey() {
      this.ensureActiveBooking();
    },
    scheduleVersion(newValue, oldValue) {
      const option = this.activeBookingOption;
      const schedule = option && Array.isArray(option.schedule) ? option.schedule : [];
      const nextFirst = schedule.length ? schedule[0].date : null;
      const pendingAdvance = this.pendingDateAdvance;

      this.resetScheduleNavigation();
      this.rebuildSlotCacheForBooking(this.activeBookingOption);
      if (!this.initialScheduleFirstDate && nextFirst) {
        this.initialScheduleFirstDate = nextFirst;
      }
      if (pendingAdvance === 'next') {
        this.canNavigateBackward = true;
        this.pendingDateAdvance = null;
        this.pendingScheduleVersion = null;
      } else if (pendingAdvance === 'prev') {
        if (this.initialScheduleFirstDate && nextFirst === this.initialScheduleFirstDate) {
          this.canNavigateBackward = false;
        } else if (nextFirst) {
          this.canNavigateBackward = true;
        }
        this.pendingDateAdvance = null;
        this.pendingScheduleVersion = null;
      } else if (this.initialScheduleFirstDate && nextFirst) {
        this.canNavigateBackward = nextFirst !== this.initialScheduleFirstDate;
      }
      this.bumpScheduleRenderKey('scheduleVersion_change', {
        newValue,
        oldValue,
      });
    },
    activeBookingOption(newOption, oldOption) {
      const newKey = newOption && newOption.key !== undefined && newOption.key !== null
        ? String(newOption.key)
        : null;
      const oldKey = oldOption && oldOption.key !== undefined && oldOption.key !== null
        ? String(oldOption.key)
        : null;

      if (newKey && oldKey && newKey === oldKey) {
        this.prefetchSlotsForCurrentView();
        return;
      }

      this.resetScheduleNavigation();
      this.prefetchSlotsForCurrentView();
      this.initialScheduleFirstDate = null;
      this.canNavigateBackward = false;
    },
    'activeBookingOption.schedule'(newSchedule, oldSchedule) {
      if (Array.isArray(newSchedule) && newSchedule.length) {
        this.rebuildSlotCacheForBooking(this.activeBookingOption);
      }

      const previousLength = Array.isArray(oldSchedule) ? oldSchedule.length : 0;
      const nextLength = Array.isArray(newSchedule) ? newSchedule.length : 0;
      const previousGroups = Math.ceil(previousLength / 4);
      const nextGroups = Math.ceil(nextLength / 4);
      const previousFirst = Array.isArray(oldSchedule) && oldSchedule.length ? oldSchedule[0]?.date : null;
      const nextFirst = Array.isArray(newSchedule) && newSchedule.length ? newSchedule[0]?.date : null;
      const previousLast = Array.isArray(oldSchedule) && oldSchedule.length ? oldSchedule[oldSchedule.length - 1]?.date : null;
      const nextLast = Array.isArray(newSchedule) && newSchedule.length ? newSchedule[newSchedule.length - 1]?.date : null;
      if (!this.pendingDateAdvance) {
        const windowChanged = (previousFirst !== nextFirst) || (previousLast !== nextLast);
        if (windowChanged) {
          this.resetScheduleNavigation();
          if (!this.initialScheduleFirstDate && nextFirst) {
            this.initialScheduleFirstDate = nextFirst;
            this.canNavigateBackward = false;
          }
        }
        this.bumpScheduleRenderKey('scheduleChange_noPending', {
          windowChanged,
        });
        return;
      }

      const windowShiftedForward = this.pendingDateAdvance === 'next'
        && previousLength > 0
        && nextLength > 0
        && previousLast !== nextLast;
      const windowShiftedBackward = this.pendingDateAdvance === 'prev'
        && previousLength > 0
        && nextLength > 0
        && previousFirst !== nextFirst;

      if (windowShiftedForward || windowShiftedBackward) {
        this.resetDateWindow('scheduleChange_replaceWindow');
        this.pendingDateAdvance = null;
        this.pendingScheduleVersion = null;
        this.lastKnownTotalDateGroups = nextGroups;
        this.prefetchSlotsForCurrentView();
        if (windowShiftedForward && !this.initialScheduleFirstDate && nextFirst) {
          this.initialScheduleFirstDate = previousFirst ?? nextFirst;
        }
        if (windowShiftedForward) {
          this.canNavigateBackward = true;
        }
        if (windowShiftedBackward) {
          if (!this.initialScheduleFirstDate) {
            this.initialScheduleFirstDate = nextFirst ?? null;
          }
          if (this.initialScheduleFirstDate && nextFirst === this.initialScheduleFirstDate) {
            this.canNavigateBackward = false;
          }
        }
        return;
      }

      if (nextGroups <= previousGroups && !windowShiftedForward && !windowShiftedBackward) {
        const maxGroupIndex = Math.max(nextGroups - 1, 0);
        if (this.currentDateGroupIndex > maxGroupIndex) {
          this.currentDateGroupIndex = maxGroupIndex;
          this.activeDateIndex = 0;
        }
        this.lastKnownTotalDateGroups = nextGroups;
        this.pendingDateAdvance = null;
        this.pendingScheduleVersion = null;
        this.bumpScheduleRenderKey('scheduleChange_noGrowth', {
          maxGroupIndex,
        });
        return;
      }

      if (this.pendingDateAdvance === 'next') {
        if (windowShiftedForward && nextGroups <= previousGroups) {
          this.currentDateGroupIndex = Math.max(nextGroups - 1, 0);
        } else {
          this.currentDateGroupIndex = Math.min(
            this.currentDateGroupIndex + 1,
            Math.max(nextGroups - 1, 0),
          );
        }
        this.activeDateIndex = 0;
        this.prefetchSlotsForCurrentView();
        this.pendingDateAdvance = null;
        this.pendingScheduleVersion = null;
        this.lastKnownTotalDateGroups = nextGroups;
        if (windowShiftedForward && !this.initialScheduleFirstDate && nextFirst) {
          this.initialScheduleFirstDate = previousFirst ?? nextFirst;
        }
        if (windowShiftedForward) {
          this.canNavigateBackward = true;
        }

        this.bumpScheduleRenderKey('scheduleChange_advanceNext', {
          windowShiftedForward,
        });
        return;
      }

      if (this.pendingDateAdvance === 'prev') {
        if (windowShiftedBackward && nextGroups <= previousGroups) {
          this.currentDateGroupIndex = 0;
        } else {
          this.currentDateGroupIndex = Math.max(this.currentDateGroupIndex - 1, 0);
        }
        this.activeDateIndex = 0;
        this.prefetchSlotsForCurrentView();
        this.pendingDateAdvance = null;
        this.pendingScheduleVersion = null;
        this.lastKnownTotalDateGroups = nextGroups;
        if (windowShiftedBackward) {
          if (!this.initialScheduleFirstDate) {
            this.initialScheduleFirstDate = nextFirst ?? null;
          }
          if (this.initialScheduleFirstDate && nextFirst === this.initialScheduleFirstDate) {
            this.canNavigateBackward = false;
          }
        }

        this.bumpScheduleRenderKey('scheduleChange_advancePrev', {
          windowShiftedBackward,
        });
      }
    },
    totalDateGroups(newValue) {
      const maxGroupIndex = Math.max(newValue - 1, 0);

      if (!this.pendingDateAdvance) {
        if (this.currentDateGroupIndex > maxGroupIndex) {
          this.currentDateGroupIndex = maxGroupIndex;
          this.activeDateIndex = 0;
        }
        this.lastKnownTotalDateGroups = newValue;
        this.bumpScheduleRenderKey('totalDateGroups_noPending', {
          maxGroupIndex,
        });
        return;
      }

      if (this.pendingDateAdvance === 'next') {
        this.currentDateGroupIndex = maxGroupIndex;
        this.activeDateIndex = 0;
        this.prefetchSlotsForCurrentView();
        this.pendingDateAdvance = null;
        this.pendingScheduleVersion = null;
        this.lastKnownTotalDateGroups = newValue;
        this.bumpScheduleRenderKey('totalDateGroups_advanceNext');
        return;
      }

      if (this.pendingDateAdvance === 'prev') {
        this.currentDateGroupIndex = 0;
        this.activeDateIndex = 0;
        this.prefetchSlotsForCurrentView();
        this.pendingDateAdvance = null;
        this.pendingScheduleVersion = null;
        this.lastKnownTotalDateGroups = newValue;
        this.bumpScheduleRenderKey('totalDateGroups_advancePrev');
        return;
      }

      this.lastKnownTotalDateGroups = newValue;
      this.bumpScheduleRenderKey('totalDateGroups_default');
    },
    isLoggedIn(loggedIn) {
      if (!loggedIn || !this.pendingSlotSelection) {
        return;
      }
      const pending = this.pendingSlotSelection;
      this.pendingSlotSelection = null;
      this.selectTime(pending.date, pending.time);
    },
    datesLoading(newValue) {
      if (!newValue && this.pendingDateAdvance) {
        const totalGroups = this.totalDateGroups;
        if (this.lastKnownTotalDateGroups === totalGroups
          && this.pendingScheduleVersion === this.scheduleVersion) {
          this.pendingDateAdvance = null;
          this.pendingScheduleVersion = null;
        }
      }
      if (!newValue) {
        const option = this.activeBookingOption;
        const schedule = option && Array.isArray(option.schedule) ? option.schedule : [];
        const isLoading = option && option.scheduleLoading === true;
        if (!schedule.length && !isLoading) {
          this.prefetchSlotsForCurrentView();
        }
      }
    }
  },
  methods: {
    canNavigateToPreviousWindow() {
      const option = this.activeBookingOption;
      const calendarWindow = option && option.calendarWindow ? option.calendarWindow : null;
      if (calendarWindow && calendarWindow.hasPrev !== undefined && calendarWindow.hasPrev !== null) {
        return calendarWindow.hasPrev;
      }

      const minDate = new Date();
      minDate.setHours(0, 0, 0, 0);

      const schedule = this.addresses?.[this.activeAddressIndex]?.schedule ?? [];
      let firstDate = schedule.length && schedule[0] && schedule[0].date
        ? schedule[0].date
        : null;

      if (!firstDate) {
        const fallback = buildFallbackDateWindow(this.baseDate, this.currentDateGroupIndex);
        firstDate = fallback?.firstDate ?? null;
      }

      if (!firstDate) {
        return false;
      }

      const candidate = new Date(`${firstDate}T00:00:00`);
      if (Number.isNaN(candidate.getTime())) {
        return false;
      }

      return candidate.getTime() > minDate.getTime();
    },
    bumpScheduleRenderKey() {
      this.scheduleRenderKey += 1;
    },
    filteredBookingOptions(centerKey) {
      if (!this.bookingOptions.length) {
        return [];
      }

      if (!centerKey) {
        return [...this.bookingOptions];
      }

      return this.bookingOptions.filter((option) => option.centerKey === centerKey || option.centerId === null);
    },
    initializeState() {
      const centers = this.displayCenters;
      const preferredKey = this.selectedBookingKey !== null && this.selectedBookingKey !== undefined
        ? String(this.selectedBookingKey)
        : null;
      const hasSelection = preferredKey !== null;

      let defaultCenterKey = centers[0]?.key ?? null;
      if (preferredKey) {
        const matchingCenter = centers.find((center) => Array.isArray(center?.variantKeys)
          && center.variantKeys.some((key) => String(key) === preferredKey));
        if (matchingCenter) {
          defaultCenterKey = matchingCenter.key;
        }
      }
      this.activeCenterKey = defaultCenterKey;

      const options = this.filteredBookingOptions(defaultCenterKey);
      const fallbackKey = options[0]?.key ?? this.bookingOptions[0]?.key ?? null;
      if (hasSelection && options.some((option) => String(option.key) === preferredKey)) {
        this.activeBookingKey = preferredKey;
      } else if (hasSelection && fallbackKey !== null && fallbackKey !== undefined) {
        this.activeBookingKey = String(fallbackKey);
      } else {
        this.activeBookingKey = null;
      }

      this.resetScheduleNavigation();

      this.closeDurationSelector();

      this.prefetchSlotsForCurrentView();

      if (this.activeBookingKey) {
        this.$emit('booking-option-change', this.activeBookingKey, { source: 'init' });
      }

      this.lastKnownTotalDateGroups = this.totalDateGroups || 0;
      const initialOption = this.activeBookingOption;
      const initialSchedule = initialOption && Array.isArray(initialOption.schedule)
        ? initialOption.schedule
        : [];
      this.initialScheduleFirstDate = initialSchedule.length ? initialSchedule[0].date : null;
      this.canNavigateBackward = false;
    },
    ensureActiveBooking() {
      const hasSelection = this.selectedBookingKey !== null && this.selectedBookingKey !== undefined;
      if (!hasSelection && (this.activeBookingKey === null || this.activeBookingKey === undefined)) {
        return;
      }
      const options = this.filteredBookingOptions(this.activeCenterKey);
      if (!options.length) {
        this.activeBookingKey = null;
        return;
      }

      if (!options.some((option) => String(option.key) === this.activeBookingKey)) {
        const fallback = options[0]?.key ?? null;
        this.activeBookingKey = fallback !== null && fallback !== undefined ? String(fallback) : null;
      }
    },
    resetScheduleNavigation() {
      this.activeAddressIndex = 0;
      this.currentDateGroupIndex = 0;
      this.activeDateIndex = 0;
      this.visibleSchedules = this.baseVisibleSchedules;
      this.bumpScheduleRenderKey('resetScheduleNavigation');
    },
    resetDateWindow(reason = 'resetDateWindow') {
      this.currentDateGroupIndex = 0;
      this.activeDateIndex = 0;
      this.visibleSchedules = this.baseVisibleSchedules;
      this.bumpScheduleRenderKey(reason);
    },
    setActiveCenter(centerKey) {
      if (this.activeCenterKey === centerKey) {
        return;
      }
      this.activeCenterKey = centerKey;
      this.closeDurationSelector();
      this.prefetchSlotsForCurrentView();
      this.initialScheduleFirstDate = null;
      this.canNavigateBackward = false;
    },
    setActiveBooking(bookingKey) {
      const normalized = bookingKey !== null && bookingKey !== undefined ? String(bookingKey) : null;
      if (this.activeBookingKey === normalized) {
        return;
      }
      this.activeBookingKey = normalized;
      if (normalized) {
        this.$emit('booking-option-change', normalized, { source: 'user' });
      }
      this.closeDurationSelector();
      this.prefetchSlotsForCurrentView();
    },
    async openDurationSelector(options, context) {
      const normalizedOptions = normalizeDurationOptions(options);
      const contextDate = context && context.date ? context.date : null;
      const contextTime = context && context.time ? context.time : null;
      const slotInfo = context && context.slotInfo ? context.slotInfo : null;
      const startInfo = context && context.startInfo ? context.startInfo : null;
      const explicitDuration = context && context.explicitDuration !== undefined ? context.explicitDuration : null;
      const scheduleDuration = context && context.scheduleDuration !== undefined ? context.scheduleDuration : null;

      const selection = this.resolveDurationSelection(normalizedOptions, {
        explicitDuration,
        scheduleDuration,
      });
      if (!selection.options.length || !contextDate || !contextTime) {
        return;
      }

      this.durationSelector = {
        visible: true,
        options: selection.options,
        date: contextDate,
        time: contextTime,
        slotInfo,
        startInfo,
        prices: {},
        loading: true,
        selectedDuration: selection.selectedDuration,
      };

      const bookingKey = this.buildBookingKey(contextDate, contextTime);
      this.bookingInProgressKey = bookingKey;
      this.bookingInProgress = true;

      try {
        const priceMap = await this.loadDurationPrices(selection.options, {
          date: contextDate,
          time: contextTime,
          slotInfo,
          startInfo,
        });
        if (!this.durationSelector.visible) {
          return;
        }
        this.durationSelector = {
          ...this.durationSelector,
          prices: priceMap,
          loading: false,
        };
      } catch (error) {
        if (!this.durationSelector.visible) {
          return;
        }
        this.durationSelector = {
          ...this.durationSelector,
          loading: false,
        };
      } finally {
        this.bookingInProgress = false;
        this.bookingInProgressKey = null;
      }
    },
    closeDurationSelector() {
      this.durationSelector = buildDefaultDurationSelector();
    },
    selectDurationOption(duration) {
      const numericDuration = Number(duration);
      const resolvedDuration = Number.isFinite(numericDuration) ? numericDuration : null;
      this.durationSelector = {
        ...this.durationSelector,
        selectedDuration: resolvedDuration,
      };
    },
    async confirmPrebookSelection() {
      const selector = this.durationSelector;
      if (selector.selectedDuration === null || selector.selectedDuration === undefined) {
        return;
      }
      const numericDuration = Number(selector.selectedDuration);
      const resolvedDuration = Number.isFinite(numericDuration) ? numericDuration : null;

      if (!selector.date || !selector.time) {
        return;
      }

      this.closeDurationSelector();

      await this.startPrebook({
        date: selector.date,
        time: selector.time,
        slotInfo: selector.slotInfo,
        startInfo: selector.startInfo,
        explicitDuration: resolvedDuration,
      });
    },
    resolveDurationSelection(options, context) {
      const scheduleDuration = context && context.scheduleDuration !== undefined && context.scheduleDuration !== null
        ? Number(context.scheduleDuration)
        : null;
      const explicitDuration = context && context.explicitDuration !== undefined && context.explicitDuration !== null
        ? Number(context.explicitDuration)
        : null;
      const inferredDuration = this.activeDurationMinutes !== null && this.activeDurationMinutes !== undefined
        ? Number(this.activeDurationMinutes)
        : null;
      const optionDuration = this.activeBookingOption && this.activeBookingOption.durationMinutes !== undefined
        ? Number(this.activeBookingOption.durationMinutes)
        : null;

      const candidates = [];
      [explicitDuration, scheduleDuration, inferredDuration, optionDuration].forEach((value) => {
        const numeric = Number(value);
        if (Number.isFinite(numeric) && numeric > 0 && !candidates.includes(numeric)) {
          candidates.push(numeric);
        }
      });

      let durationCandidate = candidates.length ? candidates[0] : 30;
      if (!scheduleDuration && durationCandidate > 60) {
        durationCandidate = 60;
      }
      const normalizedDuration = Math.max(15, Math.min(Math.round(durationCandidate), 240));

      const nextOptions = options.slice();
      if (explicitDuration !== null && Number.isFinite(explicitDuration) && !nextOptions.includes(explicitDuration)) {
        nextOptions.unshift(explicitDuration);
      }

      if (!nextOptions.length) {
        nextOptions.push(normalizedDuration);
      }

      const preferredDuration = [inferredDuration, optionDuration].find(
        (value) => Number.isFinite(value) && value > 0,
      ) ?? null;

      let selectedDuration;
      if (explicitDuration !== null && Number.isFinite(explicitDuration)) {
        selectedDuration = explicitDuration;
      } else if (preferredDuration !== null && nextOptions.includes(preferredDuration)) {
        selectedDuration = preferredDuration;
      } else {
        selectedDuration = nextOptions[0];
      }

      return {
        options: nextOptions,
        selectedDuration,
      };
    },
    resolveTenantContext() {
      const tenantBase = this.tenantUrl ? normalizeBaseUrl(this.tenantUrl) : '';
      const effectiveTenantBase = tenantBase;
      const allowLocalCustomerId = effectiveTenantBase === '';
      return {
        effectiveTenantBase,
        allowLocalCustomerId,
      };
    },
    resolveCurrencyCode() {
      const option = this.activeBookingOption;
      if (option && option.priceLocalized && typeof option.priceLocalized.currencyCode === 'string') {
        const trimmed = option.priceLocalized.currencyCode.trim();
        if (trimmed) {
          return trimmed;
        }
      }
      return 'EUR';
    },
    resolveLoadPriceLabel(priceData, priceWithTaxes) {
      if (!priceData || typeof priceData !== 'object') {
        return '';
      }
      const useTaxes = priceWithTaxes !== false;
      const currencyCode = this.resolveCurrencyCode();
      const grossAmount = priceData.price !== undefined && priceData.price !== null
        ? Number(priceData.price)
        : null;
      const netAmount = priceData.netPrice !== undefined && priceData.netPrice !== null
        ? Number(priceData.netPrice)
        : null;
      const taxes = priceData.taxes !== undefined && priceData.taxes !== null
        ? Number(priceData.taxes)
        : null;
      let displayAmount = null;
      if (useTaxes) {
        displayAmount = Number.isFinite(grossAmount)
          ? grossAmount
          : (Number.isFinite(netAmount) ? netAmount : null);
      } else {
        displayAmount = Number.isFinite(netAmount)
          ? netAmount
          : (Number.isFinite(grossAmount) ? grossAmount : null);
      }
      const localized = {
        grossAmount: Number.isFinite(displayAmount) ? displayAmount : null,
        netAmount: Number.isFinite(netAmount) ? netAmount : null,
        taxes: Number.isFinite(taxes) ? taxes : null,
        currencyCode,
      };
      const label = resolvePriceLabel(localized, null);
      if (label) {
        return label;
      }
      if (Number.isFinite(displayAmount)) {
        return `${displayAmount.toFixed(2)} ${currencyCode}`;
      }
      return '';
    },
    async loadDurationPrices(options, context) {
      const rawBookingTypeId = this.activeBookingTypeId;
      const numericBookingTypeId = Number(rawBookingTypeId);
      const bookingTypeId = Number.isFinite(numericBookingTypeId) ? numericBookingTypeId : rawBookingTypeId;
      if (bookingTypeId === null || bookingTypeId === undefined || bookingTypeId === '') {
        return {};
      }

      const startInfo = context && context.startInfo ? context.startInfo : null;
      const fallbackDate = context && context.date ? context.date : null;
      let startDate = null;
      if (startInfo && typeof startInfo.local === 'string' && startInfo.local) {
        startDate = startInfo.local;
      } else if (startInfo && typeof startInfo.raw === 'string' && startInfo.raw) {
        startDate = startInfo.raw;
      } else if (startInfo && typeof startInfo.iso === 'string' && startInfo.iso) {
        startDate = startInfo.iso;
      } else if (fallbackDate) {
        startDate = fallbackDate;
      }

      if (!startDate) {
        return {};
      }

      const slotInfo = context && context.slotInfo ? context.slotInfo : null;
      const slotResourceId = slotInfo && slotInfo.resourceId !== undefined && slotInfo.resourceId !== null
        ? slotInfo.resourceId
        : null;
      const fallbackResourceId = this.activeResourceId !== null && this.activeResourceId !== undefined
        ? this.activeResourceId
        : null;
      const rawResourceId = slotResourceId !== null && slotResourceId !== undefined
        ? slotResourceId
        : fallbackResourceId;
      const numericResourceId = Number(rawResourceId);
      const resourceId = Number.isFinite(numericResourceId) ? numericResourceId : rawResourceId;
      if (resourceId === null || resourceId === undefined || resourceId === '') {
        return {};
      }

      const tenantContext = this.resolveTenantContext();
      const customerId = null;

      const priceMap = {};
      const basePayload = {
        startDate,
        bookingTypeId,
        customerId,
        resourceId,
      };

      const tenantUrl = tenantContext.effectiveTenantBase ? tenantContext.effectiveTenantBase : null;
      const priceWithTaxes = await resolvePriceWithTaxes(tenantUrl);
      const tasks = options.map(async (duration) => {
        try {
          const durationValue = Number(duration);
          if (!Number.isFinite(durationValue) || durationValue <= 0) {
            return;
          }
          const response = await loadPrice({
            ...basePayload,
            duration: durationValue,
          }, {
            tenantUrl,
          });
          const data = response && typeof response === 'object' && 'data' in response
            ? response.data
            : response;
          if (!data || typeof data !== 'object') {
            return;
          }
          if (data.ok === false) {
            return;
          }
          const priceData = data.price;
          if (!priceData || typeof priceData !== 'object') {
            return;
          }
          const label = this.resolveLoadPriceLabel(priceData, priceWithTaxes);
          const depositValue = Number(priceData.depositAmount);
          const depositLabel = Number.isFinite(depositValue) && depositValue > 0
            ? resolvePriceLabel({ grossAmount: depositValue, currencyCode: priceData.currencyCode ?? priceData.CurrencyCode ?? null })
            : null;
          priceMap[durationValue] = {
            label: label || null,
            raw: priceData,
            deposit: depositLabel,
          };
        } catch (error) {
          return;
        }
      });

      await Promise.all(tasks);
      return priceMap;
    },
    prefetchSlotsForCurrentView() {
      this.$nextTick(() => {
        if (!this.hasSelectedBooking) {
          return;
        }
        const option = this.activeBookingOption;
        if (!option) {
          return;
        }
        const schedule = Array.isArray(option.schedule) ? option.schedule : [];
        const isLoading = Boolean(option.scheduleLoading);
        if (!schedule.length) {
          if (!isLoading && !this.datesLoading) {
            this.$emit('request-calendar', {
              bookingKey: option.key,
              date: this.baseDate ?? null,
            });
          }
          return;
        }
        this.rebuildSlotCacheForBooking(option);
      });
    },
    requestCalendarRefresh(bookingKey, targetDateIso) {
      const normalizedKey = bookingKey !== null && bookingKey !== undefined ? String(bookingKey) : null;
      if (!normalizedKey) {
        return;
      }

      const baseDate = targetDateIso || (this.baseDate ? this.baseDate : null);
      this.$emit('request-calendar', {
        bookingKey: normalizedKey,
        date: baseDate,
        force: true,
      });
    },
    rebuildSlotCacheForBooking(option) {
      const bookingKey = option?.key !== null && option?.key !== undefined ? String(option.key) : null;
      if (!bookingKey) {
        return;
      }

      const schedule = Array.isArray(option?.schedule) ? option.schedule : [];
      if (!schedule.length) {
        return;
      }

      const location = option && option.location ? option.location : null;
      const tenant = option && option.tenant ? option.tenant : null;
      const fallbackResourceName = location && location.name
        ? location.name
        : (tenant && tenant.name
          ? tenant.name
          : (this.doctor && this.doctor.tenantName ? this.doctor.tenantName : null));
      const preferredDuration = option && option.durationMinutes !== undefined && option.durationMinutes !== null
        ? Number(option.durationMinutes)
        : null;

      const nextBookingCache = buildSlotCache(schedule, {
        resourceId: option ? option.resourceId : null,
        resourceName: fallbackResourceName,
        preferredDuration,
      });

      this.slotCache = {
        ...this.slotCache,
        [bookingKey]: nextBookingCache,
      };
    },
    isSelectableTime(value, dateIso = null) {
      if (!value || value === '-') {
        return false;
      }

      if (!dateIso) {
        return true;
      }

      const bookingKey = this.activeBookingKey !== null && this.activeBookingKey !== undefined
        ? String(this.activeBookingKey)
        : null;
      const slotInfo = resolveSlotInfo(this.slotCache, bookingKey, dateIso, value);
      if (!slotInfo) {
        return true;
      }

      const hasDurations = Array.isArray(slotInfo.durations) && slotInfo.durations.length > 0;
      const hasPreferred = Number.isFinite(slotInfo.preferedDuration) && slotInfo.preferedDuration > 0;

      return slotInfo.isAvailable && (hasDurations || hasPreferred);
    },
    guessDurationFromSchedule(dateIso, time) {
      if (!dateIso || !time) {
        return null;
      }
      const bookingKey = this.activeBookingKey !== null && this.activeBookingKey !== undefined
        ? String(this.activeBookingKey)
        : null;
      const slotInfo = resolveSlotInfo(this.slotCache, bookingKey, dateIso, time);
      const slotDurations = collectSlotDurations(slotInfo);
      if (slotDurations.length) {
        return slotDurations[0];
      }

      const schedule = this.addresses?.[this.activeAddressIndex]?.schedule ?? [];
      const entry = getScheduleEntryByDate(schedule, this.slotCache, bookingKey, dateIso);
      if (!entry || !Array.isArray(entry.times) || entry.times.length === 0) {
        return null;
      }

      const sortedTimes = [...entry.times]
        .map((value) => {
          const raw = typeof value === 'string' ? value : String(value ?? '');
          const trimmed = raw.trim();
          const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
          if (match) {
            const hours = match[1].padStart(2, '0');
            const minutes = match[2].padStart(2, '0');
            return `${hours}:${minutes}`;
          }
          return trimmed;
        })
        .filter((value) => value.includes(':'))
        .sort();
      if (!sortedTimes.length) {
        return null;
      }

      const index = sortedTimes.indexOf(time);
      if (index === -1) {
        return null;
      }

      const currentMinutes = parseTimeToMinutesValue(sortedTimes[index]);
      if (currentMinutes === null) {
        return null;
      }

      if (index < sortedTimes.length - 1) {
        const nextMinutes = parseTimeToMinutesValue(sortedTimes[index + 1]);
        if (nextMinutes !== null && nextMinutes > currentMinutes) {
          return nextMinutes - currentMinutes;
        }
      }

      if (index > 0) {
        const previousMinutes = parseTimeToMinutesValue(sortedTimes[index - 1]);
        if (previousMinutes !== null && currentMinutes > previousMinutes) {
          return currentMinutes - previousMinutes;
        }
      }

      return null;
    },
    prevDateGroup() {
      if (this.disablePrev || this.scheduleLoading) {
        return;
      }

      if (this.currentDateGroupIndex > 0) {
        this.currentDateGroupIndex--;
        this.activeDateIndex = 0;
        this.prefetchSlotsForCurrentView();
        return;
      }

      const requested = this.requestAdditionalDates('prev');
      if (requested) {
        this.pendingDateAdvance = 'prev';
        this.pendingScheduleVersion = this.scheduleVersion;
      }
    },
    nextDateGroup() {
      if (this.scheduleLoading) {
        return;
      }

      if (this.currentDateGroupIndex < this.totalDateGroups - 1) {
        this.currentDateGroupIndex++;
        this.activeDateIndex = 0;
        this.prefetchSlotsForCurrentView();
        return;
      }

      const requested = this.requestAdditionalDates('next');
      if (requested) {
        this.pendingDateAdvance = 'next';
        this.pendingScheduleVersion = this.scheduleVersion;
      }
    },
    setActiveDate(index) {
      if (this.scheduleLoading) {
        return;
      }
      this.activeDateIndex = index;
    },
    formatDate(dateStr) {
      const rawLocale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'es-ES';
      const translate = typeof this.$t === 'function' ? this.$t.bind(this) : null;
      return formatScheduleDateParts(dateStr, rawLocale, translate);
    },
    buildBookingKey(date, time) {
      return `${date ?? ''}__${time ?? ''}`;
    },
    isButtonLoading(date, time) {
      return this.bookingInProgress && this.bookingInProgressKey === this.buildBookingKey(date, time);
    },
    async selectTime(date, time) {
      if (!this.isSelectableTime(time, date) || this.bookingInProgress) {
        return;
      }

      const isLogged = CustomerHelper.isUserLogged ? CustomerHelper.isUserLogged() : Boolean(CustomerHelper.getUser());
      if (!isLogged) {
        this.pendingSlotSelection = { date, time };
        CustomerHelper.loginIfNotLogged?.(false);
        return;
      }

      const startInfo = buildStartDateTimeValue(date, time);
      const targetDateIso = startInfo.local
        ? startInfo.local.substring(0, 10)
        : (startInfo.raw ? startInfo.raw.substring(0, 10) : null);

      const bookingKey = this.activeBookingKey !== null && this.activeBookingKey !== undefined
        ? String(this.activeBookingKey)
        : null;
      const slotInfo = resolveSlotInfo(this.slotCache, bookingKey, targetDateIso, startInfo.time);
      const slotDurations = collectSlotDurations(slotInfo);
      const scheduleDuration = this.guessDurationFromSchedule(targetDateIso, startInfo.time);

      await this.openDurationSelector(slotDurations, {
        date,
        time,
        slotInfo,
        startInfo,
        explicitDuration: slotDurations.length === 1 ? slotDurations[0] : null,
        scheduleDuration,
      });
    },
    async startPrebook(context) {
      const {
        date,
        time,
        slotInfo: slotInfoParam = null,
        startInfo: startInfoParam = null,
        explicitDuration = null,
      } = context || {};

      if (!date || !time) {
        return;
      }

      const isLogged = CustomerHelper.isUserLogged ? CustomerHelper.isUserLogged() : Boolean(CustomerHelper.getUser());
      if (!isLogged) {
        CustomerHelper.loginIfNotLogged?.(false);
        return;
      }

      let startInfo = startInfoParam;
      if (!startInfo) {
        startInfo = buildStartDateTimeValue(date, time);
      }

      const targetDateIso = startInfo.local
        ? startInfo.local.substring(0, 10)
        : (startInfo.raw ? startInfo.raw.substring(0, 10) : null);

      let slotInfo = slotInfoParam;
      const bookingKey = this.activeBookingKey !== null && this.activeBookingKey !== undefined
        ? String(this.activeBookingKey)
        : null;
      if (!slotInfo) {
        slotInfo = resolveSlotInfo(this.slotCache, bookingKey, targetDateIso, startInfo.time);
      }

      this.closeDurationSelector();

      this.bookingInProgressKey = this.buildBookingKey(date, time);
      this.bookingInProgress = true;

      try {
        const scheduleDuration = this.guessDurationFromSchedule(targetDateIso, startInfo.time);
        const isOnline = this.activeAllowRemote;
        const result = await prebookAppointment({
          date,
          time,
          slotInfo,
          startInfo,
          explicitDuration,
          scheduleDuration,
          activeBookingOption: this.activeBookingOption,
          activeBookingTypeId: this.activeBookingTypeId,
          activeBookingTypeName: this.activeBookingTypeName,
          activeDurationMinutes: this.activeDurationMinutes,
          activeResourceId: this.activeResourceId,
          doctor: this.doctor,
          isOnline,
          addresses: this.addresses,
          activeAddressIndex: this.activeAddressIndex,
          consultationFeeLabel: this.consultationFeeLabel,
          tenantUrl: this.tenantUrl,
          tenantName: this.tenantName,
          tenantId: this.tenantId,
          specialty: this.specialty,
          defaultTherapy: this.defaultTherapy,
          defaultTherapyType: this.defaultTherapyType,
          mainAddress: this.mainAddress,
        });

        if (!result) {
          return;
        }

        if (!result.ok) {
          const messages = extractErrorMessages(
            result.payload,
            this.$t('The selected time slot is no longer available. Please choose another time.'),
          );
          this.showErrorModal(messages);
          this.requestCalendarRefresh(bookingKey, targetDateIso);
          return;
        }

        this.closeErrorModal();
        this.requestCalendarRefresh(bookingKey, targetDateIso);
        const payloadModel = result.payload && typeof result.payload === 'object'
          ? result.payload.model
          : null;
        const serviceFree = payloadModel && typeof payloadModel === 'object'
          ? payloadModel.serviceFree === true
          : false;
        if (serviceFree) {
          getCheckoutStore().setCheckoutContext(null);
          router.push({ name: 'UpcomingAppointments' });
          return;
        }
        getCheckoutStore().setCheckoutContext(result.checkoutContext || null);
        if (result.saleModel) {
          CartService.addSale(result.saleModel);
        }
        router.push({ name: 'CheckoutPrebookSuccess' });
      } catch (error) {
        getCheckoutStore().setCheckoutContext(null);
        const errorPayload = error && typeof error === 'object' && 'data' in error
          ? error.data
          : (error && typeof error === 'object' && 'response' in error && error.response && error.response.data)
            ? error.response.data
            : (error && typeof error === 'object' && error.message ? error.message : null);
        const messages = extractErrorMessages(
          errorPayload,
          this.$t('The selected time slot is no longer available. Please choose another time.'),
        );
        this.showErrorModal(messages);
        this.requestCalendarRefresh(bookingKey, targetDateIso);
      } finally {
        this.bookingInProgress = false;
        this.bookingInProgressKey = null;
      }
    },
    loadMoreSchedules() {
      if (this.visibleSchedules < this.activeTimesMax) {
        this.visibleSchedules += 4;
      }
    },
    showLessSchedules() {
      this.visibleSchedules = this.baseVisibleSchedules;
      this.bumpScheduleRenderKey('showLessSchedules');
    },
    requestAdditionalDates(direction) {
      const dateWindow = Array.isArray(this.currentDates) ? this.currentDates : [];
      let firstDate = dateWindow.length && dateWindow[0] && dateWindow[0].iso
        ? dateWindow[0].iso
        : null;
      let lastDate = dateWindow.length && dateWindow[dateWindow.length - 1] && dateWindow[dateWindow.length - 1].iso
        ? dateWindow[dateWindow.length - 1].iso
        : null;

      if (!firstDate || !lastDate) {
        const schedule = Array.isArray(this.activeBookingOption?.schedule)
          ? this.activeBookingOption.schedule
          : [];
        firstDate = schedule.length && schedule[0] && schedule[0].date ? schedule[0].date : null;
        lastDate = schedule.length && schedule[schedule.length - 1] && schedule[schedule.length - 1].date
          ? schedule[schedule.length - 1].date
          : null;
      }

      if (!firstDate || !lastDate) {
        const fallback = buildFallbackDateWindow(this.baseDate, this.currentDateGroupIndex);
        if (!fallback) {
          return false;
        }
        firstDate = fallback.firstDate;
        lastDate = fallback.lastDate;
      }

      const option = this.activeBookingOption;
      const calendarWindow = option && option.calendarWindow ? option.calendarWindow : null;
      let targetDate = null;
      if (calendarWindow) {
        if (direction === 'prev' && calendarWindow.prevDate) {
          targetDate = calendarWindow.prevDate;
        }
        if (direction === 'next' && calendarWindow.nextDate) {
          targetDate = calendarWindow.nextDate;
        }
      }

      this.$emit('request-more-dates', {
        direction,
        bookingKey: this.activeBookingKey,
        firstDate,
        lastDate,
        targetDate,
      });
      return true;
    },
    showErrorModal(messages) {
      const validMessages = Array.isArray(messages) && messages.length
        ? messages.map((msg) => String(msg).trim()).filter((msg) => msg.length)
        : [];

      this.errorModalMessages = validMessages.length
        ? [...new Set(validMessages)]
        : [this.$t('The selected time slot is no longer available. Please choose another time.')];
      this.errorModalVisible = true;
    },
    closeErrorModal() {
      this.errorModalVisible = false;
      this.errorModalMessages = [];
    }
  }
}
